// @flow
import { format, fromUnixTime } from 'date-fns';
import React, { Component } from 'react';
import facetTypes from '../../utils/search/facetTypes';
import isEqual from 'react-fast-compare';
import SearchBreadcrumbCategories from './SearchBreadcrumbCategories';
import styles from './SearchBreadcrumbPills.module.scss';

type Props = $ReadOnly<{|
    clearAllParamsExceptCategories: Function,
    distance: any,
    facetOptionChecked: Function,
    facets: any[],
    isCategoryLanding: boolean,
    publishDate: any,
    ranges: any,
    removeCountryAndDistance: Function,
    removeFacetById: Function,
    removePublishedDate: Function,
    taxonomyAllSelected: Function,
    taxonomyParentSelected: Function,
|}>;

export default class SearchBreadcrumbPills extends Component<Props> {
    static defaultProps = {
        isCategoryLanding: false,
    };

    shouldComponentUpdate = (nextProps: Props) => !isEqual(this.props, nextProps);

    safePush = (result: any, filters: any) => {
        if (result) {
            filters.push(result);
        }

        return filters;
    };

    renderFilters = () => {
        const filtersWithoutCategories = this.props.facets.filter(facet => facet.id !== facetTypes.CATEGORY_ID);
        let filters = [];
        filtersWithoutCategories.forEach(facet => {
            if (facet.options) {
                filters = this.safePush(this.renderMultiselect(facet.id, facet.options), filters);
            } else if (facet.categories) {
                filters = this.safePush(this.renderTaxonomy(facet), filters);
            }
        });

        return filters;
    };

    renderTaxonomy = (facet: any) => {
        if (!facet.categories.facets || !facet.categories.facets.some(f => f.selected)) {
            return null;
        }

        return (
            <div key="taxonomy" className={styles.filter}>
                {facet.categories.facets[0].name}
                <span
                    className={styles.remove}
                    onClick={() => this.props.taxonomyAllSelected(facet.id, facet.categories.facets[0].id)}
                >
                    &times;
                </span>
            </div>
        );
    };

    renderDistanceFilter = () => {
        const { distance } = this.props;
        if (distance && distance.space && distance.zipCode) {
            return (
                <div key="distanceFilter" className={styles.filter}>
                    <span>
                        within {distance.space}
                        mi of {distance.zipCode}{' '}
                    </span>
                    <span className={styles.remove} onClick={() => this.props.removeCountryAndDistance()}>
                        &times;
                    </span>
                </div>
            );
        }

        return null;
    };

    renderRangeFilters = () => {
        const { ranges } = this.props;
        if (ranges && (ranges.currentBid || ranges.estimate || ranges.hammerPrice)) {
            const { currentBid, estimate, hammerPrice } = ranges;

            return (
                <div key="rangeFilters" className={styles['range-filters-container']}>
                    {currentBid && this.renderRangeFilter(currentBid, 'Current Bid:', 'currentBid')}
                    {estimate && this.renderRangeFilter(estimate, 'Estimate:', 'estimate')}
                    {hammerPrice && this.renderRangeFilter(hammerPrice, 'Sold For:', 'hammerPrice')}
                </div>
            );
        }

        return null;
    };

    renderRangeFilter = (range: any, displayName: string, facetId: string) => {
        let displayElement;

        if (range.min && range.max) {
            displayElement = `$${range.min} - $${range.max}`;
        } else if (range.max) {
            displayElement = `At Most $${range.max}`;
        } else if (range.min) {
            displayElement = `At Least $${range.min}`;
        }

        return (
            <div className={styles.filter}>
                <span>
                    {displayName} {displayElement}{' '}
                </span>
                <span className={styles.remove} onClick={() => this.props.removeFacetById(facetId)}>
                    &times;
                </span>
            </div>
        );
    };

    renderMultiselect = (facetId: string, options: any) => {
        const multiSelectFilters = options
            .filter(option => option.selected)
            .map((option, index) => {
                let clickHandler;

                if (facetId === facetTypes.COUNTRY_CODE_ID) {
                    clickHandler = () => this.props.removeCountryAndDistance();
                } else {
                    clickHandler = () => this.props.facetOptionChecked(facetId, option.id, false);
                }

                return (
                    <div key={index} className={styles.filter}>
                        <span>{option.name}</span>
                        <span className={styles.remove} onClick={() => clickHandler()}>
                            &times;
                        </span>
                    </div>
                );
            });

        return multiSelectFilters.length > 0 ? multiSelectFilters : null;
    };

    renderPublishedDate = (publishedDate: any) => {
        if (publishedDate && publishedDate.min) {
            const date = fromUnixTime(publishedDate.min);
            const formattedDate = format(date, 'MMM dd, yyyy');
            return (
                <div className={styles.filter}>
                    <span>{formattedDate} - Today </span>
                    <span className={styles.remove} onClick={() => this.props.removePublishedDate()}>
                        &times;
                    </span>
                </div>
            );
        }

        return null;
    };

    render() {
        const { facets, isCategoryLanding, publishDate, taxonomyParentSelected } = this.props;

        const filters = this.renderFilters();
        const distanceFilter = this.renderDistanceFilter();
        const rangeFilters = this.renderRangeFilters();
        const renderClearAll = filters.length > 0 || distanceFilter !== null || rangeFilters !== null;
        const publishDateFilter = this.renderPublishedDate(publishDate);

        // deselect all facets
        return (
            <div className={styles['filters-wrapper']}>
                <SearchBreadcrumbCategories
                    facets={facets}
                    taxonomyParentSelected={taxonomyParentSelected}
                    isCategoryLanding={isCategoryLanding}
                />
                {filters}
                {distanceFilter}
                {rangeFilters}
                {publishDateFilter}
                {renderClearAll && (
                    <span>
                        <a className={styles['clear-all']} onClick={() => this.props.clearAllParamsExceptCategories()}>
                            Clear All
                        </a>
                    </span>
                )}
            </div>
        );
    }
}
